import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'

export interface SDRDoc {
  id: string
  title: string
  description: string
  ncc_module: string | null
  regulatory_framework: string | null
  external_link: string | null
  file_path: string | null
  file_name: string | null
  file_type: string | null
  file_size: number | null
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface SDRFilters {
  q?: string
  ncc_module?: string
  regulatory_framework?: string
  only_active?: boolean
}

export interface SDRFacets {
  ncc_modules: string[]
  regulatory_frameworks: string[]
}

const KEY = 'sales-dataroom'

export function useSDRList(filters: SDRFilters) {
  return useQuery({
    queryKey: [KEY, 'list', filters],
    queryFn: async () => {
      const { data } = await api.get<SDRDoc[]>('/v1/sales-dataroom', { params: filters })
      return data
    },
  })
}

export function useSDRFacets() {
  return useQuery({
    queryKey: [KEY, 'facets'],
    queryFn: async () => {
      const { data } = await api.get<SDRFacets>('/v1/sales-dataroom/facets')
      return data
    },
    staleTime: 60_000,
  })
}

export function useSDRCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<SDRDoc>) => {
      const { data } = await api.post<SDRDoc>('/v1/sales-dataroom', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] })
    },
  })
}

export function useSDRUpdate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<SDRDoc> }) => {
      const { data } = await api.patch<SDRDoc>(`/v1/sales-dataroom/${id}`, payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] })
    },
  })
}

export function useSDRDelete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/sales-dataroom/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] })
    },
  })
}

export interface SDRUploadResult {
  file_path: string
  file_name: string
  file_type: string
  file_size: number
}

export async function sdrUploadFile(file: File): Promise<SDRUploadResult> {
  const fd = new FormData()
  fd.append('file', file)
  const { data } = await api.post<SDRUploadResult>('/v1/sales-dataroom/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}
